import type { BlockType } from '@unifyapps/defs/types/block';
import { memo, useEffect, useMemo } from 'react';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import DependencyFlowHelper from '../../../../helper/DependencyFlowHelper';
import type { DataSourceRecordStoreState } from '../../../../stores/DataSourceRecordStore';
import { BlockLifecycleStatus } from '../../../../stores/GlobalStateStore';
import { getIsDataSourceAnEntityType } from '../../../../utils/entityDataSource';
import {
  useDSCActions,
  useDSCAllowedDataSourceResourceNames,
  useDSCBlockLifecycles,
  useDSCBlocks,
  useDSCDataSourceRecordStore,
  useDSCGlobalStateStore,
} from '../../context/selectors';
import AggregationMetadataQueryNode from './AggregationMetadataQueryNode/AggregationMetadataQueryNode';
import { getDataSourcesForAggregationMetadata } from './utils';

function createAggregationMetadataDependencyGraph({
  blocks,
  blockLifecycles,
  dataSources,
  allowedDataSourceResourceNames,
}: {
  blocks?: Record<string, BlockType>;
  dataSources: DataSourceRecordStoreState['dataSources'];
  blockLifecycles?: Record<string, BlockLifecycleStatus | undefined>;
  allowedDataSourceResourceNames: Set<string>;
}) {
  const dataSourcesForAggregationMetadata = new Set<string>();
  const nodeExecuteDataSources = new Set<string>();

  const processDataSource = (dataSourceId: string) => {
    // for entity type datasourceId we fetch the metadata upfront because we need them for tags cell type in table
    if (getIsDataSourceAnEntityType(dataSourceId)) {
      dataSourcesForAggregationMetadata.add(dataSourceId);
    } else {
      const dataSource = dataSources[dataSourceId];

      // manual data sources do not need to be executed at runtime through our code, should be triggered by the user
      if (DataSourceHelper.isManualDataSource(dataSource)) {
        return;
      }

      const resourceName = dataSource?.properties.context?.resourceName ?? '';
      // here we are checking that the dataSourceId we want metadata from needs to be executed through normal execute/node endpoint
      if (allowedDataSourceResourceNames.has(resourceName)) {
        nodeExecuteDataSources.add(dataSourceId);
      } else {
        dataSourcesForAggregationMetadata.add(dataSourceId);
      }
    }
  };

  if (blocks) {
    Object.values(blocks).forEach((block) => {
      if (
        !blockLifecycles?.[block.id] ||
        blockLifecycles[block.id] === BlockLifecycleStatus.Unmounted
      ) {
        return;
      }

      const dataSourceIds = getDataSourcesForAggregationMetadata(block, dataSources) ?? [];

      if (!dataSourceIds.length) {
        return;
      }

      dataSourceIds.forEach(processDataSource);
    });
  } else {
    // App-level logic: process all data sources directly
    // when no blocks found, currently either when page is empty or it is an app level data source
    Object.entries(dataSources).forEach(([dataSourceId, dataSource]) => {
      if (!dataSource) return;
      processDataSource(dataSourceId);
    });
  }

  return {
    dataSourcesForAggregationMetadata,
    nodeExecuteDataSources,
  };
}

function AggregationMetadataHandler() {
  const blocks = useDSCBlocks();
  const dataSources = useDSCDataSourceRecordStore().hooks.useDataSources();
  const blockLifecycles = useDSCBlockLifecycles();
  const dsDependencyFlow = useDSCGlobalStateStore().hooks.dataSource.useDependencyFlow();
  const { addDependencyFlows } = useDSCActions();
  const allowedDataSourceResourceNames = useDSCAllowedDataSourceResourceNames();

  const { dataSourcesForAggregationMetadata, nodeExecuteDataSources } = useMemo(() => {
    return createAggregationMetadataDependencyGraph({
      blocks,
      blockLifecycles,
      dataSources: dataSources ?? {},
      allowedDataSourceResourceNames,
    });
  }, [blocks, dataSources, blockLifecycles, allowedDataSourceResourceNames]);

  const dataSourcesToRequest = useMemo(() => {
    const nodeExecuteDataSourcesArray = Array.from(nodeExecuteDataSources);
    return nodeExecuteDataSourcesArray.filter((dataSourceId) =>
      DependencyFlowHelper.isDependencyUnstaged(dsDependencyFlow, dataSourceId),
    );
  }, [dsDependencyFlow, nodeExecuteDataSources]);

  useEffect(() => {
    // for the data sources that need to be executed to get the aggregation metadata,
    // we request them so that they are executed normally using the AutomaticDataSourceHandler
    if (dataSourcesToRequest.length) {
      const adaptedPayload = DependencyFlowHelper.createStagingDependencyFlow({
        ids: dataSourcesToRequest,
      });
      addDependencyFlows(adaptedPayload);
    }
  }, [dataSourcesToRequest, addDependencyFlows]);

  return (
    <>
      {Array.from(dataSourcesForAggregationMetadata).map((dataSourceId) => {
        return <AggregationMetadataQueryNode dataSourceId={dataSourceId} key={dataSourceId} />;
      })}
    </>
  );
}

export default memo(AggregationMetadataHandler);
